import { z } from 'zod';

import ContentStatusEnum from '@@constants/ContentStatus';
import PublicationStatusEnum from '@@constants/PublicationStatus';
import { LANG_CODES } from '@@lib/i18n/constants';
import { type UnityElement } from '@@plate/serialization/unity/groups';

export const Entity = z.object({
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});

export const UnityRichTextElement = z.custom<UnityElement>();

export const UnityRichText = z.object({
    richTextFormat: z.array(UnityRichTextElement),
});
export type UnityRichText = z.infer<typeof UnityRichText>;

// This type should be something like:
// export const RichText = z.array(RichTextElement);
// But in its current state, it is way too complex to be inferred by TS and Zod, and triggers "excessive depth" errors.
// We will need to rethink our complex types, maybe switch to interfaces in some complex cases,
// as extended interfaces have better caching than types.
export const RichText = z.array(z.any());
export type RichText = z.infer<typeof RichText>;
// export const RichText = z.array(RichTextElement);

export const ContentLocale = z.enum([LANG_CODES.GERMAN, LANG_CODES.FRENCH]);
export type ContentLocale = z.infer<typeof ContentLocale>;

export const UiLocale = z.enum([LANG_CODES.ENGLISH, LANG_CODES.GERMAN, LANG_CODES.FRENCH]);
export type UiLocale = z.infer<typeof UiLocale>;

export const Point = z.object({
    x: z.number(),
    y: z.number(),
});

export type Point = z.infer<typeof Point>;

export const CropMarks = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
});

export type CropMarks = z.infer<typeof CropMarks>;

const CropVariant = z.object({
    cropMarks: CropMarks.nullish(),
});

const DefaultTeaserImageVariants = z.object({
    '1:1': CropVariant,
    '3:2': CropVariant,
    '16:9': CropVariant,
});

const OpenGraphImageVariants = z.object({
    '2:1': CropVariant,
});

const ImageVariants = z.union([
    DefaultTeaserImageVariants,
    OpenGraphImageVariants,
    z.record(z.string(), z.never()),
]);

export type ImageVariants = z.infer<typeof ImageVariants>;

export const Image = z.object({
    focusPoint: Point.nullish(),
    cropMarks: CropMarks.nullish(),
    url: z.string().nullish(),
    elvisId: z.string().nullish(),
    caption: z.any().optional(), // TODO: should be Element[]
    credit: z.string().optional(),
    name: z.string().nullish(),
    naturalWidth: z.number().nullish(),
    naturalHeight: z.number().nullish(),
    width: z.number().optional(),
    height: z.number().optional(),
    mimetype: z.string().nullish(),
    variants: ImageVariants.nullish(),
    style: z
        .object({
            imageType: z.union([z.literal('image'), z.literal('infographic')]),
        })
        .optional(),
});

export type Image = z.infer<typeof Image>;
export const ImageWithoutUrl = Image.partial({ url: true });

export type ImageWithoutUrl = z.infer<typeof ImageWithoutUrl>;

export const Creator = z.object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
});

export type Creator = z.infer<typeof Creator>;

export const ContentStatus = z.nativeEnum(ContentStatusEnum);
export type ContentStatus = z.infer<typeof ContentStatus>;

export const PublicationStatus = z.nativeEnum(PublicationStatusEnum);
export type PublicationStatus = z.infer<typeof PublicationStatus>;

export const ContentOwner = z.enum(['UNITY']);

export const ContentTypes = {
    ARTICLES: 'articles',
    TICKERS: 'tickers',
    SLIDESHOWS: 'slideshows',
    VIDEOS: 'videos',
    LINKS: 'links',
    EMBEDS: 'embeds',
    BREAKIGNEWS: 'breakingnews',
} as const;

export const ContentType = z.nativeEnum(ContentTypes);
export type ContentType = z.infer<typeof ContentType>;

export const Ids = z.union([z.string(), z.array(z.string()), z.number(), z.array(z.number())]);
